// src/components/forms/AddProgram.jsx
import React, { useState, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../../contexts/DashboardContext';
import api from '../../services/api';
import Loader from '../Utils/Loader';
import { buildPersonOptions, filterPersonOptions, personOptionMatches } from '../../utils/personSelect';
import { formatImageBytes, normalizeProgramImage } from '../../utils/normalizePersonImage';
import '../../css/addProgram.css';

const AddProgram = () => {
  const navigate = useNavigate();
  const { people, topics } = useDashboard();

  /* Deriva opzioni dal context (si aggiornano automaticamente) */
  const peopleOptions = useMemo(
    () => buildPersonOptions(people),
    [people]
  );

  const topicsOptions = useMemo(
    () => (topics || [])
      .map(t => ({ value: t.id, label: t.name }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [topics]
  );

  const [formData, setFormData] = useState({
    title: '',
    protagonists: [],
    expectedGuests: [],
    topics: [],
    description: '',
    imageBase64: '',
    episodeLogoBase64: '',
  });

  const [preview, setPreview] = useState(null);
  const [episodeLogoPreview, setEpisodeLogoPreview] = useState(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [isEpisodeLogoLoading, setIsEpisodeLogoLoading] = useState(false);
  const [imageInfo, setImageInfo] = useState(null);
  const [episodeLogoInfo, setEpisodeLogoInfo] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');


  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleMultiSelectChange = (selected, field) => {
    const values = Array.isArray(selected)
      ? selected.map(o => o.value)
      : selected
        ? [selected.value]
        : [];
    setFormData({ ...formData, [field]: values });
  };

  const handleFileChange = async e => {
    const file = e.target.files[0];
    if (!file) {
      setPreview(null);
      setImageInfo(null);
      setFormData(prev => ({ ...prev, imageBase64: '' }));
      return;
    }

    setError('');
    setIsImageLoading(true);
    try {
      const normalized = await normalizeProgramImage(file);
      setPreview(normalized.dataUrl);
      setImageInfo(normalized);
      setFormData(prev => ({ ...prev, imageBase64: normalized.dataUrl }));
    } catch (err) {
      console.error('Errore nella normalizzazione della copertina:', err);
      setPreview(null);
      setImageInfo(null);
      setFormData(prev => ({ ...prev, imageBase64: '' }));
      setError(err?.message || 'Impossibile preparare la copertina.');
      e.target.value = '';
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleEpisodeLogoFileChange = async e => {
    const file = e.target.files[0];
    if (!file) {
      setEpisodeLogoPreview(null);
      setEpisodeLogoInfo(null);
      setFormData(prev => ({ ...prev, episodeLogoBase64: '' }));
      return;
    }

    setError('');
    setIsEpisodeLogoLoading(true);
    try {
      const normalized = await normalizeProgramImage(file);
      setEpisodeLogoPreview(normalized.dataUrl);
      setEpisodeLogoInfo(normalized);
      setFormData(prev => ({ ...prev, episodeLogoBase64: normalized.dataUrl }));
    } catch (err) {
      console.error('Errore nella normalizzazione del logo episodio:', err);
      setEpisodeLogoPreview(null);
      setEpisodeLogoInfo(null);
      setFormData(prev => ({ ...prev, episodeLogoBase64: '' }));
      setError(err?.message || 'Impossibile preparare il logo episodio.');
      e.target.value = '';
    } finally {
      setIsEpisodeLogoLoading(false);
    }
  };

  const loadPeopleOptions = (inputValue, callback) => {
    callback(filterPersonOptions(peopleOptions, inputValue));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      protagonists: [],
      expectedGuests: [],
      topics: [],
      description: '',
      imageBase64: '',
      episodeLogoBase64: '',
    });
    setPreview(null);
    setEpisodeLogoPreview(null);
    setImageInfo(null);
    setEpisodeLogoInfo(null);
    setError('');
    // Scrolla in cima per mostrare messaggio
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (isImageLoading || isEpisodeLogoLoading || isSubmitting) return;

    setIsSubmitting(true);

    try {
      await api.post('/programs', formData, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 45000,
      });
      setSuccess('Programma aggiunto con successo!');
      resetForm();
    } catch (err) {
      console.error("Errore nell'aggiunta del programma:", err);
      const message = err?.code === 'ECONNABORTED'
        ? 'Il caricamento ha impiegato troppo tempo. Riprova senza chiudere la pagina.'
        : err?.response?.data?.error || "Errore nell'aggiunta del programma";
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="addprogram-container">
        <Loader />
      </div>
    );
  }

  return (
    <div className="addprogram-container">
      {error && <p className="error">{error}</p>}
      {success && <p className="success">{success}</p>}

      <h2>Aggiungi nuovo programma</h2>
      <form onSubmit={handleSubmit} className="addprogram-form">
        <label>
          Titolo:
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            placeholder="Inserisci il titolo"
          />
        </label>

        <label>
          Protagonisti:
          <AsyncSelect
            isMulti
            name="protagonists"
            loadOptions={loadPeopleOptions}
            defaultOptions={peopleOptions.slice(0, 20)}
            filterOption={personOptionMatches}
            onChange={opts => handleMultiSelectChange(opts, 'protagonists')}
            value={peopleOptions.filter(o =>
              formData.protagonists.includes(o.value)
            )}
            placeholder="Seleziona protagonisti..."
            classNamePrefix="select"
          />
        </label>

        <label>
          Ospiti attesi:
          <AsyncSelect
            isMulti
            name="expectedGuests"
            loadOptions={loadPeopleOptions}
            defaultOptions={peopleOptions.slice(0, 20)}
            filterOption={personOptionMatches}
            onChange={opts => handleMultiSelectChange(opts, 'expectedGuests')}
            value={peopleOptions.filter(o =>
              formData.expectedGuests.includes(o.value)
            )}
            placeholder="Seleziona ospiti attesi..."
            classNamePrefix="select"
          />
        </label>

        <label>
          Argomenti:
          <Select
            isMulti
            name="topics"
            options={topicsOptions}
            onChange={opts => handleMultiSelectChange(opts, 'topics')}
            value={topicsOptions.filter(o =>
              formData.topics.includes(o.value)
            )}
            placeholder="Seleziona argomenti..."
            classNamePrefix="select"
          />
        </label>

        <label>
          Carica immagine:
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            disabled={isImageLoading || isEpisodeLogoLoading || isSubmitting}
          />
        </label>
        <div className="preview-container">
          {isImageLoading ? <Loader /> : preview && <img src={preview} alt="Anteprima" />}
        </div>
        {imageInfo && (
          <small>
            Copertina ottimizzata: {imageInfo.width}×{imageInfo.height}, {' '}
            {formatImageBytes(imageInfo.originalBytes)} → {formatImageBytes(imageInfo.outputBytes)}
          </small>
        )}

        <label>
          Logo Episodio:
          <input
            type="file"
            accept="image/*"
            onChange={handleEpisodeLogoFileChange}
            disabled={isImageLoading || isEpisodeLogoLoading || isSubmitting}
          />
        </label>
        <div className="preview-container">
          {isEpisodeLogoLoading ? <Loader /> : episodeLogoPreview && <img src={episodeLogoPreview} alt="Anteprima Logo Episodio" style={{ maxWidth: '200px' }} />}
        </div>
        {episodeLogoInfo && (
          <small>
            Logo ottimizzato: {episodeLogoInfo.width}×{episodeLogoInfo.height}, {' '}
            {formatImageBytes(episodeLogoInfo.originalBytes)} → {formatImageBytes(episodeLogoInfo.outputBytes)}
          </small>
        )}

        <label>
          Descrizione:
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            placeholder="Inserisci una breve descrizione..."
          />
        </label>

        <button type="submit" disabled={isImageLoading || isEpisodeLogoLoading || isSubmitting}>
          {isImageLoading || isEpisodeLogoLoading ? 'Preparazione immagine…' : 'Aggiungi Programma'}
        </button>
      </form>

      {success && (
        <button onClick={() => navigate('/dashboard')} className="redirect-btn">
          Vai alla Dashboard
        </button>
      )}
    </div>
  );
};

export default AddProgram;
